/**
 * Orders API ↔ domain mapping.
 *
 * Wire shapes copied from the backend's own guides —
 * `docs/pos-api/api-docs/orders-log.md`, `orders-reporting.md` and
 * `orders-reversals.md`, all three verified against **live HTTP on a dev
 * tenant with shapes verbatim**, so these are responses that actually landed.
 *
 * Four things here exist because of the documented traps:
 *
 * · **`orders/` is unwrapped as pagination shape A** (trap #3) — the six-key
 *   page-number envelope. `summary/` and `form-options/` are flat objects and
 *   `export/` is its own envelope with no pages at all, so each has its own
 *   read rather than a shared list helper.
 * · **Decimals arrive as strings** (trap #8) — `"162.00"`. Parsed here, at the
 *   boundary, so no screen ever sees a numeric string.
 * · **Branching is on `error`, never `message`** (trap #6).
 * · **There is no `updated_at` round-trip** (trap #4 does *not* apply): this is
 *   the one module with no optimistic-concurrency parameter. `updated_at` is
 *   carried for change detection only and must never be sent back.
 *
 * No `"use server"` — that directive permits async exports only, and this file
 * holds types, mappers and the error-copy helper.
 */

import type {
  KitchenBadge,
  OrderChannel,
  OrderDetail,
  OrderFormOptions,
  OrderItem,
  OrderPage,
  OrderPayment,
  OrderRecord,
  OrderStatus,
  OrderStatusCounts,
  OrderSummary,
  ServiceChargeBasis,
} from "@/components/order-history/types";
import type { CurrencyContext, StatTile } from "@/components/shared/StatCard";
import { formatCurrency } from "@/lib/currency";

// ── Wire shapes ─────────────────────────────────────────────────────────────

/** One row of `GET orders/`. The detail shape adds the drawer's fields. */
export interface ApiOrderRow {
  id: string;
  order_number: string;
  channel: string;
  /** `""` on takeaway — a frozen copy, never a join. */
  table_label: string;
  /** decimal(10,2) as a **string**, VAT included. */
  total: string;
  status: string;
  /** Derived from the lines, and `null` whenever nothing has fired. */
  kitchen_badge: string | null;
  placed_at: string | null;
  staff_name: string;
  /** The `Staff` row id. Always present — every order has a server. */
  staff_id: string;
  /**
   * A **presigned, short-lived** GET URL, or `null` when that member uploaded
   * no photo.
   *
   * ⚠️ **Live, not frozen.** `staff_name` is a snapshot of the sale;
   * the photo resolves through the order's staff key, so changing a picture
   * changes the face on every order that member ever took. It becomes frozen
   * with no contract change once order creation can stamp one
   * (`answers/DONE-2026-08-11-orders-staff-avatar-answers.md` §2) — which is
   * also why it must never reach a printed receipt.
   *
   * Treat it as a rendering token: never persisted, never cached, never signed
   * here. Re-read the row for a fresh one.
   */
  staff_photo_url: string | null;
  can_cancel: boolean;
  can_refund: boolean;
  updated_at: string;
}

/** One add-on on a line — frozen at sale time, like the line itself. */
export interface ApiOrderAddon {
  id: string;
  name: string;
  /** decimal string — an uplift on `unit_price`, not a total. */
  price_delta: string;
}

/** One line of `GET orders/{id}/`. */
export interface ApiOrderItem {
  id: string;
  item_name: string;
  /** A plain integer here — the 3dp quantity family does not appear in orders. */
  qty: number;
  unit_price: string;
  line_total: string;
  size: string;
  kitchen_message: string;
  fire_state: string;
  station: string;
  add_ons: ApiOrderAddon[];
}

/** One tender. `payments` is a LIST even when the design draws one block. */
export interface ApiOrderPayment {
  id: string;
  method: string;
  amount: string;
  status: string;
  card_brand: string;
  card_last4: string;
  transaction_id: string;
  /** `null` on a single tender. */
  split_index: number | null;
  person_label: string | null;
  paid_at: string | null;
}

/** `GET orders/{id}/` — the list row plus the drawer's payload. */
export interface ApiOrderDetail extends ApiOrderRow {
  invoice_no: string;
  guest_count: number;
  business_date: string;
  /** `null` on every order today. */
  currency_code: string | null;
  sub_total: string;
  vat_rate: string;
  vat_amount: string;
  service_charge_basis: string;
  /** `null` unless the basis is `on_total_cost`. */
  service_charge_rate: string | null;
  discount_type: string;
  discount_value: string | null;
  discount_amount: string;
  service_charge_amount: string;
  items: ApiOrderItem[];
  payments: ApiOrderPayment[];
  refundable_amount: string;
}

/** `GET orders/summary/` — six flat figures, never paginated. */
export interface ApiOrderSummary {
  total_orders: number;
  revenue: string;
  /** `null` when nothing contributed to revenue. */
  average_order_value: string | null;
  cancelled_orders: number;
  /** Sum of `total` over the same cancelled rows — 2dp string, `"0.00"`, never null. */
  cancelled_value: string;
  /** `null` = **no comparison available**, not zero. */
  revenue_delta: string | null;
}

/**
 * `GET orders/status-counts/` — a flat object, no envelope and no paging.
 *
 * `counts` carries **all nine** `?status=` tokens, `draft` included, and each
 * one equals the row count its own filter returns.
 */
export interface ApiOrderStatusCounts {
  counts: Record<string, number>;
  total: number;
}

/** `GET orders/form-options/` — the four vocabularies, nested under `options`. */
export interface ApiOrderFormOptions {
  options?: {
    status?: { value: string; label: string }[];
    channel?: { value: string; label: string }[];
    cancellation_reason?: { value: string; label: string }[];
    refund_reason?: { value: string; label: string }[];
  };
}

/** Pagination shape A — the six-key page-number envelope `orders/` returns. */
export interface ApiOrderPageEnvelope {
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: ApiOrderRow[];
}

// ── Errors ──────────────────────────────────────────────────────────────────

/** The error envelope every rejection uses: `{ error, message, details }`. */
export interface ApiOrderErrorBody {
  error?: string;
  message?: string;
  details?: {
    /** 409 `refund_exceeds_balance` — the real ceiling and what was asked for. */
    remaining?: string;
    requested?: string;
    /** 400 `export_too_large`. */
    row_count?: number;
    maximum?: number;
    /** 409 `order_not_cancellable`. */
    status?: string;
    order_number?: string;
    /** DRF field errors, e.g. `{ reason: ["This field is required."] }`. */
    [field: string]: unknown;
  } | null;
}

/** What `serverPostCall` throws — status plus the parsed body. */
export type OrderApiError = Error & {
  status?: number;
  response?: ApiOrderErrorBody & { code?: string };
};

/**
 * Turn a rejection into copy the screen can show.
 *
 * **Branches on `error`, never `message`** (trap #6). Where the backend's own
 * sentence is written for display it is returned as-is — the reversal guide is
 * explicit that `cash_refund_not_available_here` must be shown verbatim,
 * because it names the only place the refund can actually happen.
 */
export function orderErrorMessage(error: unknown, fallback: string): string {
  const err = error as OrderApiError;
  const code = err?.response?.error ?? err?.response?.code;
  const details = err?.response?.details;
  const message = err?.response?.message;

  switch (code) {
    // ── Reversals (409) ────────────────────────────────────────────────────
    case "order_not_cancellable":
      // The copy names the operator's next step — refund it instead
      return (
        message ?? "This order can no longer be cancelled. Refund it instead."
      );
    case "order_not_refundable":
      return (
        message ??
        "This order has not been paid, so there is nothing to refund."
      );
    case "refund_exceeds_balance": {
      // `details` carries the real ceiling, which is more useful than the
      // generic sentence — a second manager may have refunded in between
      const remaining = details?.remaining;
      return remaining
        ? `That is more than is left to refund on this order — ${remaining} remains.`
        : (message ?? "That is more than is left to refund on this order.");
    }
    case "cash_refund_not_available_here":
      // Shown verbatim on purpose. A split bill with ONE cash leg is refused
      // whole, so this fires on orders that look part-refundable
      return (
        message ??
        "A cash payment can only be refunded at the terminal, from an open cash drawer."
      );

    // ── Export (400 / 429) ─────────────────────────────────────────────────
    case "export_too_large": {
      const rows = details?.row_count;
      const max = details?.maximum;
      return rows && max
        ? `That selection is ${rows.toLocaleString()} rows, over the ${max.toLocaleString()} limit. Narrow the filters and try again.`
        : (message ??
            "That selection is too large to export. Narrow the filters and try again.");
    }
    case "rate_limited":
      // The export has its OWN 10/min scope — a double-clicked Download is the
      // usual cause, which is why the button disables while in flight
      return (
        message ?? "You've made too many requests. Please wait and try again."
      );

    // ── Access ─────────────────────────────────────────────────────────────
    case "permission_denied":
      // `orders.cancel` and `orders.refund` are separate codenames and neither
      // is in the supervisor baseline — holding one does not grant the other
      return "You do not have permission to do that.";
    case "not_found":
      return message ?? "This order could not be found.";

    // ── Field-level rejections ─────────────────────────────────────────────
    case "validation_error": {
      // DRF field errors: `{ reason: ["This field is required."] }`. Also how
      // the four server-resolved fields are refused, so the field name here is
      // the actionable part
      if (details) {
        for (const value of Object.values(details)) {
          if (Array.isArray(value) && typeof value[0] === "string") {
            return value[0];
          }
        }
      }
      return message ?? fallback;
    }

    default:
      return message ?? fallback;
  }
}

// ── Mappers ─────────────────────────────────────────────────────────────────

/** `"162.00"` → `162`; `null`/`""`/nonsense → `0`. */
export function toNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Same parse, but `null` survives as `null` — for the fields where it means something. */
function toNullableNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Map an API row onto the table's `OrderRecord`. */
export function mapOrder(api: ApiOrderRow): OrderRecord {
  return {
    id: api.id,
    orderNumber: api.order_number,
    channel: api.channel as OrderChannel,
    tableLabel: api.table_label ?? "",
    total: toNumber(api.total),
    status: api.status as OrderStatus,
    // Anything outside the two known badges is treated as "nothing to show" —
    // `null` here means no chip, not unknown
    kitchenBadge:
      api.kitchen_badge === "preparing" || api.kitchen_badge === "ready"
        ? (api.kitchen_badge as KitchenBadge)
        : null,
    placedAt: api.placed_at,
    staffName: api.staff_name,
    staffId: api.staff_id,
    staffAvatarUrl: api.staff_photo_url,
    canCancel: api.can_cancel,
    canRefund: api.can_refund,
    updatedAt: api.updated_at,
  };
}

/** Map one line, with its modifiers expanded as the drawer renders them. */
function mapOrderItem(api: ApiOrderItem): OrderItem {
  return {
    id: api.id,
    name: api.item_name,
    qty: api.qty,
    unitPrice: toNumber(api.unit_price),
    lineTotal: toNumber(api.line_total),
    size: api.size ?? "",
    kitchenMessage: api.kitchen_message ?? "",
    fireState: api.fire_state as OrderItem["fireState"],
    station: api.station ?? "",
    addOns: (api.add_ons ?? []).map((addon) => ({
      id: addon.id,
      name: addon.name,
      priceDelta: toNumber(addon.price_delta),
    })),
  };
}

/** Map one tender. `split_index` / `person_label` stay null on a single payment. */
function mapOrderPayment(api: ApiOrderPayment): OrderPayment {
  return {
    id: api.id,
    method: api.method,
    amount: toNumber(api.amount),
    status: api.status,
    cardBrand: api.card_brand ?? "",
    cardLast4: api.card_last4 ?? "",
    transactionId: api.transaction_id ?? "",
    splitIndex: api.split_index,
    personLabel: api.person_label,
    paidAt: api.paid_at,
  };
}

/** Map the detail response — the row plus items, tenders and the money block. */
export function mapOrderDetail(api: ApiOrderDetail): OrderDetail {
  return {
    ...mapOrder(api),
    invoiceNo: api.invoice_no ?? "",
    guestCount: api.guest_count ?? 0,
    businessDate: api.business_date,
    currencyCode: api.currency_code,
    subTotal: toNumber(api.sub_total),
    vatRate: toNumber(api.vat_rate),
    vatAmount: toNumber(api.vat_amount),
    serviceChargeBasis: (api.service_charge_basis ?? "") as ServiceChargeBasis,
    // Stays null on `on_per_person`, where there is no percentage to show
    serviceChargeRate: toNullableNumber(api.service_charge_rate),
    serviceChargeAmount: toNumber(api.service_charge_amount),
    discountType: api.discount_type ?? "",
    discountValue: toNullableNumber(api.discount_value),
    discountAmount: toNumber(api.discount_amount),
    items: (api.items ?? []).map(mapOrderItem),
    payments: (api.payments ?? []).map(mapOrderPayment),
    refundableAmount: toNumber(api.refundable_amount),
  };
}

/** Unwrap pagination shape A and map a full page, preserving the totals. */
export function mapOrderPage(
  result: ApiOrderPageEnvelope | null,
  pageSize: number,
): OrderPage {
  if (!result || !Array.isArray(result.results)) {
    return { orders: [], total: 0, page: 1, pageSize };
  }
  return {
    orders: result.results.map(mapOrder),
    total: result.count ?? result.results.length,
    page: result.current_page ?? 1,
    pageSize,
  };
}

/** Map the KPI response. Nulls are carried through — they are not zeroes. */
export function mapOrderSummary(api: ApiOrderSummary | null): OrderSummary {
  return {
    totalOrders: api?.total_orders ?? 0,
    revenue: toNumber(api?.revenue),
    averageOrderValue: toNullableNumber(api?.average_order_value),
    cancelledOrders: api?.cancelled_orders ?? 0,
    cancelledValue: toNumber(api?.cancelled_value),
    revenueDelta: toNullableNumber(api?.revenue_delta),
  };
}

/**
 * Map the segment counts. An empty map on failure, so the segments render with
 * no numbers rather than with wrong ones.
 */
export function mapOrderStatusCounts(
  api: ApiOrderStatusCounts | null,
): OrderStatusCounts {
  return { byStatus: api?.counts ?? {}, total: api?.total ?? null };
}

/** Map the four vocabularies. Empty lists on failure — never a hardcoded enum. */
export function mapOrderFormOptions(
  api: ApiOrderFormOptions | null,
): OrderFormOptions {
  const options = api?.options ?? {};
  return {
    status: options.status ?? [],
    channel: options.channel ?? [],
    cancellationReason: options.cancellation_reason ?? [],
    refundReason: options.refund_reason ?? [],
  };
}

// ── The one query string ────────────────────────────────────────────────────

/**
 * Filters the Order History screen narrows by. All optional.
 *
 * Lives here rather than in a fetcher because **three endpoints take the same
 * query string** — `orders/`, `orders/summary/` and `orders/export/` — and
 * neither of the last two defines filters of its own. A filtered table above
 * unfiltered cards would describe two different sets of records.
 */
export interface OrderQuery {
  /**
   * One of the **nine** `form-options/` values. Six query the order's
   * commercial state, three query the kitchen axis (the lines). An unknown
   * value is a 400, never an empty page.
   */
  status?: string;
  /** `dine_in` · `takeaway`. An unknown value is a 400. */
  channel?: string;
  /** Case-insensitive substring over order/invoice number, table and staff name. */
  search?: string;
  /**
   * A preset token — `today`, `yesterday`, `this_week`, `this_month`,
   * `last_7_days`, `last_30_days`, `last_90_days`, `this_year`.
   *
   * **Mutually exclusive with the explicit range**: sending both is a 400.
   */
  period?: string;
  /** Inclusive bounds on the **trading day**, `YYYY-MM-DD`. */
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Turn the screen's query into the API's params.
 *
 * Two rules are enforced here rather than at each call site:
 *
 * · **An explicit range wins over a preset**, and the preset is then dropped —
 *   sending both is a 400, and the range is the more specific intent (it is
 *   also the only thing that produces a `revenue_delta`).
 * · **"Today" is resolved server-side.** A venue's trading day can cross
 *   midnight, so at 01:00 a browser computing today's date silently drops the
 *   whole late evening. The token goes over the wire, never a computed date.
 */
export function buildOrderParams(
  query: OrderQuery,
  { paginate = true }: { paginate?: boolean } = {},
): Record<string, string> {
  const params: Record<string, string> = {};

  if (paginate) {
    params.page = String(query.page ?? 1);
    params.page_size = String(query.pageSize ?? DEFAULT_ORDER_PAGE_SIZE);
  }
  if (query.status) params.status = query.status;
  if (query.channel) params.channel = query.channel;
  if (query.search?.trim()) params.search = query.search.trim();

  const hasRange = Boolean(query.dateFrom && query.dateTo);
  if (hasRange) {
    params.business_date_from = query.dateFrom as string;
    params.business_date_to = query.dateTo as string;
  } else if (query.period) {
    params.period = query.period;
  }

  return params;
}

/**
 * Rows per page. The server defaults to 20 and accepts up to **100**, which is
 * why the largest option stops there — asking for more is a validation error.
 */
export const ORDER_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const DEFAULT_ORDER_PAGE_SIZE = ORDER_PAGE_SIZE_OPTIONS[0];

/**
 * The status segments the design draws above the table, in its order.
 *
 * They span **both** axes the one `?status=` parameter accepts: `completed` is
 * the order's commercial state, while `preparing` and `ready` query the kitchen
 * (the lines). Their labels come from `form-options/` — only the selection is
 * fixed here, because it is a design decision rather than a contract one.
 */
export const ORDER_TAB_STATUSES = ["completed", "preparing", "ready"];

/**
 * The window the screen opens on, matching the header's idle label.
 *
 * A **token**, never a date computed here: a venue's trading day can cross
 * midnight, so at 01:00 the browser's "today" silently drops the whole late
 * evening from both the table and the cards.
 */
export const DEFAULT_ORDER_PERIOD = "today";

// ── KPI tiles ───────────────────────────────────────────────────────────────

/**
 * Build the four headline tiles from the summary.
 *
 * Takes `currency` because **`revenue_delta` is absolute money, not a
 * percentage** — the API deliberately carries no percentage, and computing one
 * client-side from two calls would disagree with the backend at window
 * boundaries. So it renders as a formatted caption rather than `StatTile`'s
 * `deltaPercent`, and a `null` delta renders as a dash exactly as the reporting
 * guide requires: never `0`, never a computed percent.
 *
 * Takes `query` for one reason only: **the delta's prior window is built from
 * the dates alone and ignores `status`, `channel` and `search`.** The current
 * figure is narrowed by them; the one it is compared against is not. Rendering
 * the caption anyway compares a filtered figure against the whole venue —
 * measured on the dev tenant, filtering to Takeaway understated its growth by
 * the entire dine-in prior revenue, and a status filter flipped the delta
 * **negative**, drawing a downward trend from figures unrelated to the rows on
 * screen. So a non-date filter is treated as a fourth cause of "no comparison
 * available" and shows the same dash, which is exactly what it is.
 */
export function buildOrderStatTiles(
  summary: OrderSummary,
  currency: CurrencyContext,
  query: OrderQuery = {},
): StatTile[] {
  const money = (value: number, decimals = 2) =>
    formatCurrency(value, {
      decimals,
      currencyCode: currency.currencyCode,
      country: currency.country,
    });

  // Date filters are fine — they define the comparison. Anything else narrows
  // only one side of it.
  const narrowed = Boolean(query.status || query.channel || query.search);
  const delta = narrowed ? null : summary.revenueDelta;

  return [
    {
      key: "total-orders",
      label: "Total Orders",
      icon: "shopping-cart",
      value: summary.totalOrders,
      format: "count",
      // `summary.total_orders` counts every order in scope EXCEPT drafts, while
      // the All segment's count includes them (orders-reporting.md §1 and
      // `answers/DONE-2026-08-11-orders-status-counts` §4). Both are right and
      // they differ by the draft count, so the tile says which one it is rather
      // than leaving two numbers on one screen contradicting each other.
      caption: "Drafts excluded",
      captionTone: "muted",
    },
    {
      key: "revenue",
      label: "Revenue",
      icon: "money",
      value: summary.revenue,
      format: "money",
      // Pence, like the Average Order Value tile beside it. Rounding to the
      // pound turned 5598.23 into "£5,598", which reads as an exact takings
      // figure that disagrees with the amounts in the table below it

      // No comparison available — the delta needs an explicit date range, and a
      // preset never produces one. The caption is dropped entirely rather than
      // set to a dash: the caption sits *beside* the value, so "£5,598.23 —"
      // reads as part of the amount instead of as a missing comparison
      caption:
        delta === null
          ? undefined
          : `${delta >= 0 ? "+" : "−"}${money(Math.abs(delta))} vs previous period`,
      captionTone:
        delta === null ? undefined : delta >= 0 ? "success" : "warning",
      captionTrend: delta !== null,
    },
    {
      key: "average-order-value",
      label: "Average Order Value",
      icon: "chart-average",
      // null = no order contributed to revenue; the tile shows the zero rather
      // than vanishing, and the empty table beside it explains why
      value: summary.averageOrderValue ?? 0,
      format: "money",
      // **This tile does not divide by the Total Orders tile, and the caption is
      // what says so.** `average_order_value` is `revenue` ÷ the orders that
      // contributed to it — `completed` and `refunded` — while `total_orders`
      // counts every order but drafts (orders-reporting.md §Fields). On a window
      // with 51 orders of which 26 traded, £5,105 ÷ 26 = £196.36 and £5,105 ÷ 51
      // = £100.11; QA read the second as the right answer because nothing on the
      // card distinguished the two populations. Recomputing it here would be the
      // real defect: it would divide takings by orders that never traded and put
      // this card at odds with the backend, the till and Finance
      caption: "Completed and refunded orders",
      captionTone: "muted",
    },
    {
      key: "cancelled-orders",
      label: "Cancelled Orders",
      icon: "cart-cancel",
      value: summary.cancelledOrders,
      format: "count",
      // The count's twin, over the same rows under the same filters. Captioned
      // as value rather than loss: a cancelled order never traded, so nothing
      // here is netted off Revenue or Average Order Value
      caption: `${money(summary.cancelledValue)} cancelled`,
      captionTone: "muted",
    },
  ];
}
